import { useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { updateReorderGamesStart } from '../../../../store/redux-slices/superAdminCasinoManagement'
import { getAllCasinoCategoriesStart, getAllGamesStart, getAllSubCategoriesStart } from '../../../../store/redux-slices/tenantCasino'
import { getItem } from '../../../../utils/storageUtils'
import { getTenantId } from '../../../../utils/constants'
import useAggregatorList from '../../../../shared/hooks/useAggregatorList'
import useAggregatorCategories from '../../../../shared/hooks/useAggregatorCategories'

/**
 * A custom hook that manages the state and logic for reordering games in a casino management system.
 *
 * @returns {Object} An object containing various state variables, handlers, and utility functions for game reordering.
 *
 * @property {boolean} loading - Indicator for loading state of the tenant casino data.
 * @property {Object} reOrderedGame - An object representing the reordered game list.
 * @property {Array} reOrderedGame.rows - Array containing the reordered games.
 * @property {number} reOrderedGame.count - Total count of reordered games.
 * @property {Function} onDragEnd - Handler for the drag-and-drop logic to reorder games.
 * @property {Function} handleSave - Saves the reordered list of games to the server.
 * @property {Function} navigate - React router's navigation function to navigate between routes.
 * @property {Object} casinoGames - Object representing the casino games data.
 * @property {Array<Object>} casinoGames.rows - The list of casino game items.
 * @property {string} casinoGames.rows[].masterCasinoGameId - The unique ID of the casino game.
 * @property {string} casinoGames.rows[].orderId - The order ID of the casino game, or 'NA' if not available.
 * @property {string} casinoGames.rows[].name - The name of the casino game.
 * @property {string} casinoGames.rows[].categoryGameId - The unique ID associated with the game's category.
 * @property {number} [casinoGames.rows[].returnToPlayer] - The RTP (Return To Player) percentage of the game, if available.
 * @property {number} casinoGames.count - Total count of casino games.
 * @property {Function} handRemoveGame - Function to remove a game from the reorder list.
 * @property {Function} handleAddGame - Function to add a game to the reorder list.
 * @property {Array} casinoCategories - Array of available casino categories.
 * @property {string} categoryFilter - Currently selected category filter.
 * @property {Function} setCategoryFilter - Function to update the category filter state.
 * @property {number} totalPages - Total number of pages for pagination.
 * @property {Function} setLimit - Function to update the limit of items fetched per page.
 * @property {Function} setPage - Function to update the current page.
 * @property {number} limit - Limit of items per page.
 * @property {number} page - Current page number.
 * @property {Array} subCategories - An array of subcategories available for the selected category.
 * @property {number|null} casinoCategoryId - Currently selected casino category ID.
 * @property {Function} setCasinoCategoryId - Function to set the current casino category ID.
 * @property {Function} setReorderedGame - Function to update the reordered game state.
 * @property {string} search - Search query for filtering games.
 * @property {Function} setSearch - Function to update the search query.
 * @property {Array} aggregatorsOptions - Array of available aggregator options.
 * @property {number} selectedAggregatorId - Currently selected aggregator ID.
 * @property {Function} setSelectedAggregatorId - Function to update the selected aggregator ID.
 * @property {Array} aggregatorsCategoryOptions - Array of available aggregator category options.
 * @property {number} selectedAggregatorCategoryId - Currently selected aggregator category ID.
 * @property {Function} setSelectedAggregatorCategoryId - Function to update the selected aggregator category ID.
 */
const useGameReorder = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { loading, games, casinoCategories, subCategories } = useSelector((state) => state.tenantCasino)
  const [reOrderedGame, setReorderedGame] = useState({ rows: [], count: 0 })
  const [casinoGames, setCasinoGames] = useState({ rows: [], count: 0 })
  const [categoryFilter, setCategoryFilter] = useState('')
  const [casinoCategoryId, setCasinoCategoryId] = useState(null)
  const [limit, setLimit] = useState(15)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const totalPages = Math.ceil(games?.count / limit)

  const {
    aggregatorsOptions,
    selectedAggregatorId,
    setSelectedAggregatorId
  } = useAggregatorList({
    tenantId: getTenantId()
  })

  const {
    aggregatorsCategoryOptions,
    selectedAggregatorCategoryId,
    setSelectedAggregatorCategoryId
  } = useAggregatorCategories({
    gameAggregatorId: selectedAggregatorId,
    search
  })

  useEffect(() => {
    if (Array.isArray(games)) {
      if (reOrderedGame?.rows?.length) {
        // We are setting show flag in all the available game.
        // If a game is present in ordered list the game as show flag set to false.
        const filteredArray = games?.map((item) => {
          const exist = reOrderedGame?.rows?.find(common => common?.categoryGameId === item?.categoryGameId)
          return { ...item, show: !exist }
        }) || []
        setCasinoGames({ rows: filteredArray, count: games?.length })
      } else {
        const availableGames = []
        const orderedGames = []
        games.forEach(gameData => {
          if (gameData?.orderId) {
            availableGames.push({
              ...gameData,
              show: false
            })
            orderedGames.push({
              ...gameData,
              show: false
            })
          } else {
            availableGames.push({
              ...gameData,
              show: true
            })
          }
        })
        setCasinoGames({ rows: availableGames, count: availableGames?.length })
        setReorderedGame({ rows: orderedGames, count: orderedGames?.length })
      }
    }
  }, [games])

  useEffect(() => {
    dispatch(getAllCasinoCategoriesStart({ limit: '', pageNo: '' }))
  }, [])

  const reorder = (reorderItem, startIndex, endIndex) => {
    const result = Array.from(reorderItem)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const onDragEnd = (result) => {
    if (!result.destination) {
      return
    }

    if (result.destination.index === result.source.index) {
      return
    }

    const rows = reorder(
      reOrderedGame.rows,
      result.source.index,
      result.destination.index
    )
    setReorderedGame({ rows, count: rows.length })
  }

  const handleAddGame = (item) => {
    if (casinoGames && reOrderedGame && item) {
      setReorderedGame((oldItem) => {
        const newArray = [item, ...oldItem?.rows]
        return { rows: newArray, count: newArray.length }
      })
      setCasinoGames((oldItem) => {
        const newArray = oldItem?.rows?.map(gameItem => {
          if (gameItem.categoryGameId === item.categoryGameId) {
            return { ...gameItem, show: false }
          }
          return gameItem
        })
        return { rows: newArray, count: newArray?.length }
      })
    }
  }

  const handRemoveGame = (item) => {
    if (casinoGames && reOrderedGame && item) {
      setCasinoGames((oldItem) => {
        const newArray = oldItem?.rows?.map(gameItem => {
          if (gameItem?.categoryGameId === item?.categoryGameId) {
            return { ...gameItem, show: true }
          }
          return gameItem
        })
        return { rows: newArray, count: newArray?.length }
      })
      setReorderedGame((oldItem) => {
        const newArray = oldItem?.rows?.filter((gameItem) => gameItem?.categoryGameId !== item?.categoryGameId)
        return { rows: newArray, count: newArray?.length }
      })
    }
  }

  const handleSave = async () => {
    const orderedGames = reOrderedGame?.rows?.map((list) => list?.categoryGameId) || []

    const data = {
      order: [...orderedGames],
      tenantGameSubCategoryId: casinoCategoryId,
      tenantId: getItem('tenant-id')
    }

    await dispatch(updateReorderGamesStart({
      data,
      casinoCategoryId,
      aggregatorId: selectedAggregatorId,
      aggregatorCategoryId: selectedAggregatorCategoryId
    }))
    setReorderedGame({ rows: [], count: 0 })
  }

  useEffect(() => {
    if (casinoCategoryId) {
      dispatch(
        getAllGamesStart({
          limit: '',
          pageNo: '',
          casinoCategoryId,
          isActive: '',
          orderBy: '',
          sort: '',
          providerId: '',
          order: true,
          aggregatorId: selectedAggregatorId,
          aggregatorCategoryId: selectedAggregatorCategoryId
        })
      )
    }
  }, [casinoCategoryId, selectedAggregatorId, selectedAggregatorCategoryId])

  useEffect(() => {
    if (categoryFilter) {
      dispatch(getAllSubCategoriesStart({
        limit: '',
        pageNo: '',
        categoryId: categoryFilter,
        search: '',
        isActive: '',
        orderBy: '',
        sort: ''
      }))
    }
  }, [categoryFilter])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (categoryFilter) {
        dispatch(
          getAllGamesStart({
            limit: '',
            pageNo: '',
            casinoCategoryId,
            isActive: '',
            orderBy: '',
            sort: '',
            providerId: '',
            search,
            order: true,
            aggregatorId: selectedAggregatorId,
            aggregatorCategoryId: selectedAggregatorCategoryId
          })
        )
        setPage(1)
      }
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
  }, [search])

  return {
    loading,
    reOrderedGame,
    onDragEnd,
    handleSave,
    navigate,
    casinoGames,
    handRemoveGame,
    handleAddGame,
    casinoCategories,
    categoryFilter,
    setCategoryFilter,
    totalPages,
    setLimit,
    setPage,
    limit,
    page,
    subCategories,
    casinoCategoryId,
    setCasinoCategoryId,
    setReorderedGame,
    search,
    setSearch,
    aggregatorsOptions,
    selectedAggregatorId,
    setSelectedAggregatorId,
    aggregatorsCategoryOptions,
    selectedAggregatorCategoryId,
    setSelectedAggregatorCategoryId
  }
}

export default useGameReorder
